/**
 * The three things this panel formats, in one place.
 *
 * Money is INTEGER CENTS everywhere in this project -- the worker never sends a
 * float and neither does the schema -- so the division happens here and nowhere
 * else. A `total / 100` written at a call site is the kind of thing that ends
 * up as `total` on one screen.
 */
/**
 * PINNED TO en-US, unlike `when()` below.
 *
 * The two are not the same kind of value and the difference is worth stating,
 * because the first attempt got it wrong. A TIMESTAMP is about the operator's
 * own day -- "did this happen before lunch" -- so it is rendered in their
 * conventions. A PRICE is the same string the customer was shown: the
 * storefront is English and quotes `$18.99`, the delivery mail repeats it, and
 * a support ticket quotes it back.
 *
 * Left on the browser locale, a Turkish operator's dashboard read `$2.418,70`
 * -- a dollar sign with European separators, correct for tr-TR and one
 * translation away from every other surface in this shop. Grouping is the
 * point (`toFixed(2)` gave "$2418.70", four digits to count); adopting a
 * second set of separators was not.
 */
const USD = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

export function money(cents: number | null | undefined): string {
  if (cents == null) return "—";
  return USD.format(cents / 100);
}

/**
 * THE OPERATOR'S CLOCK IS ISTANBUL'S, AND THE WORKER'S IS NOT.
 *
 * Two separate facts, and getting either wrong moves a timestamp by hours.
 *
 * WHAT ARRIVES. D1 stores `datetime('now')` as `"2026-09-13 14:37:19"` --
 * UTC, with no zone marker. Only some of the admin API converts that to
 * ISO-with-Z: the composed camelCase reads do (`toIso` in `lib/time.ts`), and
 * the hand-SELECTed snake_case list reads hand the column through untouched.
 * So this function is handed BOTH shapes, and a string with a space and no
 * zone is parsed by V8 as LOCAL time. Orders, eSIMs, payments, the audit
 * trail, the error log and the administrator roster were therefore three
 * hours early for an operator in Istanbul -- measured, not suspected: the
 * panel read `14:37` where the wall clock read `17:37`.
 *
 * No test caught it because every fixture is written as `...T09:14:00.000Z`,
 * which is the shape the real endpoint does not send.
 *
 * WHAT IS SHOWN. Pinned to `Europe/Istanbul`, not to the browser. The panel
 * has one operator, in Istanbul, and pinning means the time in the panel, the
 * time in a screenshot of it and the time in a message about it are the same
 * time -- which they are not when the zone follows whatever machine is open.
 * `hour12: false` for the same reason: the hour is the part being read.
 *
 * The LOCALE stays the browser's. It decides the month's name, which is a
 * matter of reading comfort rather than of correctness, and nothing in this
 * project stores an operator locale to override it with.
 */
const ZONE = "Europe/Istanbul";

/** A SQL `datetime('now')` string means UTC. Anything else already says so. */
function instant(value: string): Date {
  const sql = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value);
  return new Date(sql ? `${value.replace(" ", "T")}Z` : value);
}

export function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = instant(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, {
    timeZone: ZONE,
    hour12: false,
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Just the date, for columns where the minute does not matter. */
export function day(iso: string | null | undefined): string {
  if (!iso) return "—";
  const at = instant(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleDateString(undefined, {
    timeZone: ZONE,
    year: "numeric", month: "short", day: "2-digit",
  });
}

/** A byte count from the supplier, read the way `megabytes` reads a plan. */
export function bytes(count: number | null | undefined): string {
  return count == null ? "—" : megabytes(Math.round(count / 1024 ** 2));
}

/** Megabytes as an operator reads a data allowance. */
export function megabytes(mb: number | null | undefined): string {
  if (mb == null) return "—";
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`;
}

/**
 * The price box's parser: reads what `centsAsInput` writes.
 *
 * NOT the inverse of `money`, and it does not try to be. `money` groups
 * thousands ("$1,000.00") because grouping is the point of it; this refuses a
 * comma, because "5,00" is five dollars in half of Europe and five hundred
 * cents in the other half. The form fills its boxes with `centsAsInput`, which
 * never groups, and that pair round-trips exactly across the whole range the
 * worker accepts.
 *
 * FLOATING POINT IS THE WHOLE DIFFICULTY. `18.99 * 100` is
 * 1898.9999999999998 in IEEE 754, and `Math.trunc` of that is 1898 -- a
 * package that silently sells for a cent less than the operator typed, on
 * every price ending in 9. `Math.round` is correct for every value this field
 * accepts, which is bounded at two decimal places by the check below.
 *
 * The regex, not `parseFloat`: `parseFloat("18.99 or so")` is 18.99, and
 * `parseFloat("1e3")` is 1000. A price field that accepts prose accepts a
 * typo. Returns null for anything it cannot read, so the caller shows the
 * field as invalid rather than writing a guess.
 */
export function centsFrom(text: string): number | null {
  const trimmed = text.trim().replace(/^\$/, "");
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  const cents = Math.round(Number(trimmed) * 100);
  // Twenty digits pass the pattern and are not an integer any more once
  // multiplied. The same guard `countFrom` in the package form has.
  return Number.isSafeInteger(cents) ? cents : null;
}

/** Cents as the operator edits them: no symbol, no grouping, always two places. */
export function centsAsInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** English, like every other name this panel shows. */
const REGIONS = new Intl.DisplayNames(["en"], { type: "region" });

/** A country code as a name, "GB" as "United Kingdom"; the code if unknown. */
export function country(code: string | null | undefined): string {
  if (!code) return "—";
  try {
    return REGIONS.of(code) ?? code;
  } catch {
    return code;
  }
}

/** A whole number, or null. Digits only, for the reason `centsFrom` gives. */
export function countFrom(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isSafeInteger(value) ? value : null;
}
